import {
  Button,
  DataTable,
  DataTableColumn,
  DataTableDataType,
  FormatterType,
  Icon,
  SortFilters,
  createCssVariableString,
  tableSort,
  useLoading,
} from "best-common-react";
import React from "react";
import styled from "styled-components";
import { deleteEventOverrides, saveEventOverrides } from "../../api/RequesTixApi";
import { defaultAccess, defaultCutoff } from "../../constants/CutoffConstants";
import { DepartmentByGameDTO } from "../../types/Department";
import { EventTimingTypeDTO } from "../../types/EventTiming";
import { GameDTO } from "../../types/Game";
import { convertFromEventTypeDTO, convertToEventTypeDTO } from "../../util/CutoffUtil";
import { useCutoffTime as getCutoffTime } from "../../util/TimeUtil";
import EventAccessAccordion from "./EventAccessAccordion";
import EventAccessBulkOverride from "./EventAccessBulkOverride";
import OverrideDeleteModal from "./OverrideDeleteModal";
import OverrideSaveModal from "./OverrideSaveModal";

const StrikeThrough = styled.span.attrs({ className: "bcr-ms-3" })`
  text-decoration: line-through;
  color: ${createCssVariableString("onColorsOnSurfaceLow")};
`;

type CutoffOverrideForm = {
  active?: boolean;
  personalCutoffDate?: Date | null;
  personalCutoffHoursBefore?: number;
  personalCutoffUseDate?: boolean;
  businessCutoffDate?: Date | null;
  businessCutoffHoursBefore?: number;
  businessCutoffUseDate?: boolean;
};

type EventAccessProps = {
  event: GameDTO;
  departments: DepartmentByGameDTO[];
  reload: () => Promise<any>;
};

export type EventAccessData = DataTableDataType & {
  departmentId: number;
  override: {
    isOverride: boolean;
    department: DepartmentByGameDTO;
    onAdd: (department: DepartmentByGameDTO) => void;
    onEdit: (department: DepartmentByGameDTO) => void;
    onDelete: (department: DepartmentByGameDTO) => void;
  };
  department: string;
  access: {
    time: string | null;
    override: string | null;
  };
  personalCutoff: {
    time: string | null;
    override: string | null;
  };
  businessCutoff: {
    time: string | null;
    override: string | null;
  };
  personalRequests: {
    enable: boolean;
    override: boolean | null;
  };
};

const OverrideFormatter = ({
  value: { isOverride, department, onAdd, onEdit, onDelete },
}: FormatterType<EventAccessData>) =>
  !isOverride ? (
    <div className="d-flex justify-content-end">
      <Button onClick={() => onAdd(department)}>Add Override</Button>
    </div>
  ) : (
    <div className="d-flex justify-content-end">
      <Icon iconName="fa-pencil-alt" className="fa-lg bcr-me-3" onClick={() => onEdit(department)} />
      <Icon iconName="fa-trash-alt" className="fa-lg" onClick={() => onDelete(department)} />
    </div>
  );

const AccessCutoffFormatter = ({ value: { time, override } }: FormatterType<EventAccessData>) =>
  !override ? (
    <div>{time}</div>
  ) : (
    <div>
      {override}
      {"     "}
      <StrikeThrough>{time}</StrikeThrough>
    </div>
  );

const PersonalRequestFormatter = ({ value: { enable, override } }: FormatterType<EventAccessData>) =>
  override === null ? (
    <div>{enable ? "On" : "Off"}</div>
  ) : (
    <div>
      {override ? "On" : "Off"}
      {"     "}
      <StrikeThrough>{enable ? "On" : "Off"}</StrikeThrough>
    </div>
  );

const eventAccessColumns: DataTableColumn<EventAccessData>[] = [
  { key: "override", name: "Override", readonlyFormatter: OverrideFormatter, width: 160 },
  { key: "department", name: "Department", width: 250, sortable: true },
  { key: "access", name: "Event Access (local time)", readonlyFormatter: AccessCutoffFormatter, minWidth: 250 },
  {
    key: "personalCutoff",
    name: "Personal Event Cutoff (local time)",
    readonlyFormatter: AccessCutoffFormatter,
    minWidth: 250,
  },
  {
    key: "businessCutoff",
    name: "Business Event Cutoff (local time)",
    readonlyFormatter: AccessCutoffFormatter,
    minWidth: 250,
  },
  { key: "personalRequests", name: "Personal Requests", readonlyFormatter: PersonalRequestFormatter, minWidth: 250 },
];

const EventAccess = ({ event, departments, reload }: EventAccessProps) => {
  const { setLoading } = useLoading();
  const [showSaveModal, setShowSaveModal] = React.useState(false);
  const [showDeleteModal, setShowDeleteModal] = React.useState(false);
  const [showBulkOverrideModal, setBulkOverrideModal] = React.useState(false);
  const [selectedDepartment, setSelectedDepartment] = React.useState<DepartmentByGameDTO>();
  const [selectedDepartmentIds, setSelectedDepartmentIds] = React.useState<number[]>([]);
  const [personalRequestsOverride, setPersonalRequestsOverride] = React.useState<boolean | null>(null);
  const [accessOverride, setAccessOverride] = React.useState<EventTimingTypeDTO>(defaultAccess);
  const [cutoffOverride, setCutoffOverride] = React.useState<CutoffOverrideForm>(defaultCutoff);
  const [sortFilters, setSortFilters] = React.useState<SortFilters<EventAccessData>>({
    key: "department",
    direction: "ASC",
  });
  const [isEdit, setIsEdit] = React.useState(false);
  const [hasEdited, setHasEdited] = React.useState(false);

  const timezone = event?.venue?.timeZone?.id;

  const onOverrideAdd = React.useCallback((department: DepartmentByGameDTO) => {
    setSelectedDepartment(department);
    setAccessOverride(defaultAccess);
    setCutoffOverride(defaultCutoff);
    setPersonalRequestsOverride(null);
    setIsEdit(false);
    setHasEdited(false);
    setShowSaveModal(true);
  }, []);

  const onOverrideEdit = React.useCallback((department: DepartmentByGameDTO) => {
    setSelectedDepartment(department);
    setAccessOverride(department.accessOverride ? { ...department.accessOverride, active: true } : defaultAccess);
    setCutoffOverride(
      department.cutoffOverride
        ? { ...convertFromEventTypeDTO(department.cutoffOverride), active: true }
        : defaultCutoff,
    );
    setPersonalRequestsOverride(department.personalRequestsOverride ?? null);
    setIsEdit(true);
    setHasEdited(false);
    setShowSaveModal(true);
  }, []);

  const onOverrideDelete = React.useCallback((department: DepartmentByGameDTO) => {
    setSelectedDepartment(department);
    setShowDeleteModal(true);
  }, []);

  const data: EventAccessData[] = React.useMemo(
    () =>
      departments.map((department) => {
        const {
          departmentAccess,
          accessOverride,
          departmentCutoff,
          cutoffOverride,
          personalRequests,
          personalRequestsOverride,
        } = department;

        return {
          id: department.departmentId,
          departmentId: department.departmentId,
          override: {
            isOverride: !!(accessOverride || cutoffOverride || personalRequestsOverride != null),
            department,
            onAdd: onOverrideAdd,
            onEdit: onOverrideEdit,
            onDelete: onOverrideDelete,
          },
          department: department.departmentName,
          access: {
            time: departmentAccess
              ? getCutoffTime(event, departmentAccess.useDate, departmentAccess.date!, departmentAccess.hoursBefore)
              : "",
            override: accessOverride
              ? getCutoffTime(event, accessOverride.useDate, accessOverride.date!, accessOverride.hoursBefore)
              : null,
          },
          personalCutoff: {
            time: departmentCutoff
              ? getCutoffTime(
                  event,
                  departmentCutoff.personal.useDate,
                  departmentCutoff.personal.date!,
                  departmentCutoff.personal.hoursBefore,
                )
              : "",
            override: cutoffOverride
              ? getCutoffTime(
                  event,
                  cutoffOverride.personal.useDate,
                  cutoffOverride.personal.date!,
                  cutoffOverride.personal.hoursBefore,
                )
              : null,
          },
          businessCutoff: {
            time: departmentCutoff
              ? getCutoffTime(
                  event,
                  departmentCutoff.business.useDate,
                  departmentCutoff.business.date!,
                  departmentCutoff.business.hoursBefore,
                )
              : "",
            override: cutoffOverride
              ? getCutoffTime(
                  event,
                  cutoffOverride.business.useDate,
                  cutoffOverride.business.date!,
                  cutoffOverride.business.hoursBefore,
                )
              : null,
          },
          personalRequests: {
            enable: personalRequests,
            override: personalRequestsOverride,
          },
        };
      }),
    [departments, event, onOverrideAdd, onOverrideDelete, onOverrideEdit],
  );

  const displayData = React.useMemo(() => tableSort(data, sortFilters), [data, sortFilters]);

  const onAccessEdit = React.useCallback((value: EventTimingTypeDTO) => {
    setHasEdited(true);
    setAccessOverride(value);
  }, []);

  const onCutoffEdit = React.useCallback((value: CutoffOverrideForm) => {
    setHasEdited(true);
    setCutoffOverride(value);
  }, []);

  const onPersonalRequestsSelect = React.useCallback((value: boolean | null) => {
    setHasEdited(true);
    setPersonalRequestsOverride(value);
  }, []);

  const onSortChange = React.useCallback((key: keyof EventAccessData, direction) => {
    setSortFilters({ key, direction });
  }, []);

  const isSelected = React.useCallback(
    (rowIndex: number): boolean => {
      const departmentId = displayData[rowIndex]?.departmentId;
      return departmentId !== undefined && selectedDepartmentIds.includes(departmentId);
    },
    [displayData, selectedDepartmentIds],
  );

  const onSelectionChange = React.useCallback(
    (rowIndexes: number[], selected: boolean) => {
      const changedIds = rowIndexes
        .map((index) => displayData[index]?.departmentId)
        .filter((departmentId): departmentId is number => departmentId !== undefined);

      setSelectedDepartmentIds((currentIds) =>
        selected
          ? Array.from(new Set([...currentIds, ...changedIds]))
          : currentIds.filter((departmentId) => !changedIds.includes(departmentId)),
      );
    },
    [displayData],
  );

  const onOverrideSave = async () => {
    if (!selectedDepartment) return;

    const access = accessOverride.active ? accessOverride : null;
    const cutoff = cutoffOverride.active ? cutoffOverride : null;

    try {
      setLoading(true);
      await saveEventOverrides({
        departmentIds: [selectedDepartment.departmentId],
        gamePk: event.gamePk,
        personalRequestsOverride,
        accessOverride: access,
        cutoffOverride: convertToEventTypeDTO(cutoff),
      });
      setShowSaveModal(false);
      setHasEdited(false);
      await reload();
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const onBulkSave = async () => {
    try {
      setLoading(true);
      setBulkOverrideModal(false);
      setSelectedDepartmentIds([]);
      await reload();
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const onDelete = async () => {
    if (!selectedDepartment) return;

    try {
      setShowDeleteModal(false);
      await deleteEventOverrides(selectedDepartment.departmentId, String(event.gamePk));
      await reload();
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const onClose = () => {
    setSelectedDepartment(undefined);
    setShowSaveModal(false);
    setShowDeleteModal(false);
    setBulkOverrideModal(false);
    setHasEdited(false);
  };

  return (
    <>
      <OverrideSaveModal
        personalRequestsOverride={personalRequestsOverride}
        setPersonalRequestsOverride={onPersonalRequestsSelect}
        accessOverride={accessOverride}
        setAccessOverride={onAccessEdit}
        cutoffOverride={cutoffOverride}
        setCutoffOverride={onCutoffEdit}
        department={selectedDepartment?.departmentName}
        show={showSaveModal}
        onSave={onOverrideSave}
        onClose={onClose}
        isEdit={isEdit}
        hasEdited={hasEdited}
        timezone={timezone}
      />
      <OverrideDeleteModal
        department={selectedDepartment?.departmentName}
        show={showDeleteModal}
        onDelete={onDelete}
        onClose={onClose}
      />
      <EventAccessBulkOverride
        personalRequestsOverride={personalRequestsOverride}
        setPersonalRequestsOverride={onPersonalRequestsSelect}
        accessOverride={accessOverride}
        setAccessOverride={setAccessOverride}
        cutoffOverride={cutoffOverride}
        setCutoffOverride={setCutoffOverride}
        isOpen={showBulkOverrideModal}
        onSave={onBulkSave}
        onClose={onClose}
        departments={departments}
        selectedDepartmentIds={selectedDepartmentIds}
        gamePk={event.gamePk}
        timezone={timezone}
      />
      <div>
        <Button
          variant="default"
          onClick={() => {
            setBulkOverrideModal(true);
          }}
          disabled={!selectedDepartmentIds.length}
          className="bcr-mb-2"
        >
          Bulk Override
        </Button>
        <DataTable
          data={displayData}
          columns={eventAccessColumns}
          sortFunction={onSortChange}
          accordion={EventAccessAccordion}
          sortColumn={sortFilters["key"]}
          sortDirection={sortFilters["direction"]}
          rowSelection={{
            isSelected,
            onSelectionChange,
          }}
          selectAll
          statusTextOverride={(selectedCount, totalCount) =>
            selectedCount ? `${selectedCount} of ${totalCount} Department(s) Selected` : `${totalCount} Department(s)`
          }
        />
      </div>
    </>
  );
};

export default EventAccess;
